import React, { useMemo } from 'react';
import { StyledTitleText, StyledSubText, StyledForm, StyledInputWrapper, formControlOverrides } from '../RegistryProtected.styles';
import { ButtonV2, defaultTheme, DialogV2, Flex, FormControl, InputV2 } from '@withjoy/joykit';
import { ContactSuccess } from './ContactSuccess';
import { useContactHostController } from './ContactHost.controller';
import { useTranslation } from '@shared/core';
import { useColorPaletteProvider } from '@apps/guest/packages/layout-engine/layouts/ColorPaletteProvider/ColorPaletteProvider';
import { getButtonStylesWithAccentColor } from '@apps/guest/packages/layout-engine/layouts/layout.utils';
import chroma from 'chroma-js';

interface Props {
  setShowContactHost: (val: boolean) => void;
  eventId?: string;
  eventHandle: string;
  headingFontFamily?: string;
  subHeadingFontFamily?: string;
}

export const ContactHost: React.FC<Props> = ({ setShowContactHost, eventId, eventHandle, headingFontFamily, subHeadingFontFamily }) => {
  const { contactHost, contactHostFormik, contactSuccess, loading, goBack, title, subtitle, namePlaceholder, emailPlaceholder, actionButton, emailInputError, backButton } =
    useContactHostController(eventId, eventHandle, setShowContactHost);
  const { tq } = useTranslation('generalFormValidation');
  const { pageColor, pageFontColor } = useColorPaletteProvider();

  const accentColor = pageFontColor || defaultTheme.colors.black;
  const buttonStyles = useMemo(() => getButtonStylesWithAccentColor(accentColor), [accentColor]);
  const inputStyles = useMemo(
    () => ({
      borderColor: chroma(accentColor).alpha(0.35).css(),
      focusColor: accentColor,
    }),
    [accentColor],
  );

  const nameError = contactHostFormik.getFieldMeta('name').error;
  const emailError = contactHostFormik.getFieldMeta('email').error;

  return (
    <DialogV2
      isOpen={true}
      size={'sm'}
    >
      <DialogV2.Body
        paddingX={[6, 8]}
        paddingY={8}
        backgroundColor={pageColor}
        color={pageFontColor}
        borderRadius={[0, 12]}
      >
        <Flex
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
        >
          {contactSuccess ? (
            <ContactSuccess
              website={eventHandle}
              headingFontFamily={headingFontFamily}
              subHeadingFontFamily={subHeadingFontFamily}
            />
          ) : (
            <>
              <StyledTitleText
                marginBottom={5}
                marginTop={5}
                fontFamily={headingFontFamily}
              >
                {title}
              </StyledTitleText>
              <StyledSubText
                marginBottom={8}
                fontFamily={subHeadingFontFamily}
              >
                {subtitle}
              </StyledSubText>
              <StyledForm>
                <StyledInputWrapper $focusColor={inputStyles.focusColor}>
                  <FormControl
                    label={tq`Name`}
                    overrides={formControlOverrides}
                    error={nameError}
                    isInvalid={!!nameError}
                  >
                    <InputV2
                      placeholder={namePlaceholder}
                      overrides={{ Root: { props: { borderColor: inputStyles.borderColor } } }}
                      {...contactHostFormik.getFieldProps('name')}
                    />
                  </FormControl>
                </StyledInputWrapper>
                <StyledInputWrapper
                  $focusColor={inputStyles.focusColor}
                  marginBottom={8}
                >
                  <FormControl
                    label={tq`Email`}
                    error={emailError ? emailInputError : undefined}
                    isInvalid={!!emailError}
                    overrides={formControlOverrides}
                  >
                    <InputV2
                      placeholder={emailPlaceholder}
                      overrides={{ Root: { props: { borderColor: inputStyles.borderColor } } }}
                      {...contactHostFormik.getFieldProps('email')}
                    />
                  </FormControl>
                </StyledInputWrapper>
                <ButtonV2
                  shape="rounded"
                  variant="outline"
                  loading={loading}
                  fullWidth={true}
                  marginBottom={8}
                  onClick={contactHost}
                  disabled={!contactHostFormik.isValid || !contactHostFormik.dirty || loading}
                  fontFamily={subHeadingFontFamily}
                  {...buttonStyles}
                >
                  {actionButton}
                </ButtonV2>
                <ButtonV2
                  variant="ghost"
                  intent="neutral"
                  color="inherit"
                  onClick={goBack}
                  fullWidth={true}
                  fontFamily={subHeadingFontFamily}
                  _hover={{ color: buttonStyles._hover?.color, backgroundColor: 'transparent' }}
                  _active={{ color: buttonStyles._active?.color, backgroundColor: 'transparent' }}
                >
                  {backButton}
                </ButtonV2>
              </StyledForm>
            </>
          )}
        </Flex>
      </DialogV2.Body>
    </DialogV2>
  );
};
